import React, {
	useCallback, useEffect, useRef, useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	Button, Card, Empty, Input, Modal, notification, Popover, Select, Switch, Table
} from 'antd'
import i18next from 'i18next'
import { debounce, get, isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import {
	BarChartOutlined, CloseOutlined, FilePdfOutlined, PlusCircleOutlined, QrcodeOutlined
} from '@ant-design/icons'

import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import { FilterValue, SorterResult } from 'antd/lib/table/interface'
import { useReactToPrint } from 'react-to-print'
import {
	BooleanParam, NumberParam, NumericArrayParam, StringParam, useQueryParams, withDefault
} from 'use-query-params'
import { getFormInitialValues, initialize } from 'redux-form'
import { Link } from 'react-router-dom'
import {
	getJobs, IJobFirmHeader, JobTableItem
} from '../../reducers/jobs/actions'
import { renderQrScanDuration, renderQrScanLastService, renderTrustees } from './components/jobColumnRenderers'
import { useSuperOrganizationMode } from '../../utils/superOrganizationMode'
import SuperOrganizationJobs from '../superOrganization/SuperOrganizationJobs'
import { getPriceOfferHeaderOptions, IFirmHeader } from '../../reducers/priceOffers/actions'
import { history } from '../../utils/history'
import { RootState } from '../../reducers'
import { ComponentToPrint } from '../../components/QrCode/ComponentToPrint'
import { FORM, PAGINATION, USER_ROLE } from '../../utils/enums'
import TableToPrint from '../../components/QrCode/TableToPrint'
import SelectPriceOfferHeaderOptionField, { ISelectedFirmHeaderOption } from '../../components/SelectPriceOfferHeaderOptionField'
import SelectUsersField from '../../components/SelectUsersField'
import { openPdfInNewTab } from '../../utils/openPdf'

const PAGE_SIZE = 200

// specialna volba v selecte "Filter podľa s.r.o." - zakazky bez priradenej s.r.o.
const WITHOUT_FIRM_HEADER = -1

type Columns = ColumnsType<JobTableItem>

const JobsStandard = () => {
	const dispatch = useDispatch()

	const { t } = useTranslation('pages')

	const userSelectRef = useRef<any>(null)

	const [visibleGenerateQrCodeModal, setVisibleGenerateQrCodeModal] = useState(false)
	const [visibleGenerateItemListModal, setVisibleGenerateItemListModal] = useState(false)

	const [isVisibleHandoverRecordModal, setIsVisibleHandoverRecordModal] = useState(false)
	const [isVisibleHandoverMergeRecordModal, setIsVisibleHandoverMergeRecordModal] = useState(false)
	const [headerValue, setHeaderValue] = useState<ISelectedFirmHeaderOption | null | undefined>()

	const jobs = useSelector((state: RootState) => state.jobs.jobs)
	const filterInitialValues = useSelector((state: RootState) => getFormInitialValues(FORM.JOBS_FILTER)(state))

	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

	const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
		console.log('selectedRowKeys changed: ', newSelectedRowKeys)
		setSelectedRowKeys(newSelectedRowKeys)
		console.log(newSelectedRowKeys)
	}

	const rowSelection = {
		selectedRowKeys,
		onChange: onSelectChange
	}

	const isLoadingList = jobs.isLoading

	const [query, setQuery] = useQueryParams({
		search: StringParam,
		users: withDefault(NumericArrayParam, []),
		inflationPartAccepted: withDefault(BooleanParam, null),
		priceOfferFirmHeaderIDs: withDefault(NumericArrayParam, []),
		withoutFirmHeader: withDefault(BooleanParam, null),
		limit: withDefault(NumberParam, PAGINATION.defaultPageSize),
		page: withDefault(NumberParam, 1),
		orderBy: withDefault(StringParam, 'address'),
		orderDirection: withDefault(StringParam, null)
	})

	const [headerOptions, setHeaderOptions] = useState<IFirmHeader[]>([])

	useEffect(() => {
		dispatch(getPriceOfferHeaderOptions((data) => {
			setHeaderOptions(data.headers)
		}))
	}, [dispatch])

	const changeInflationFilter = (value: boolean) => { setQuery({ ...query, inflationPartAccepted: value }) }

	const handleCreateHandoverRecord = (jobIds: number[], optionHeader: ISelectedFirmHeaderOption) => {
		const pdfURL = `/api/admin/jobs/serve/hand-over-record/pdf?optionHeaderId=${optionHeader?.value}&${jobIds.map((id) => `jobsIds[]=${id}`).join('&')}`
		openPdfInNewTab(pdfURL)
		setIsVisibleHandoverRecordModal(false)
	}

	const handleCreateMergeHandoverRecord = (jobIds: number[], optionHeader: ISelectedFirmHeaderOption) => {
		const pdfURL = `/api/admin/jobs/serve/hand-over-merge-record/pdf?optionHeaderId=${optionHeader?.value}&${jobIds.map((id) => `jobsIds[]=${id}`).join('&')}`
		openPdfInNewTab(pdfURL)
		setIsVisibleHandoverRecordModal(false)
	}

	useEffect(() => {
		dispatch(getJobs({
			limit: visibleGenerateItemListModal ? 100000 : query.limit,
			page: query.page,
			queryParams: {
				search: query.search,
				users: query.users,
				orderBy: query.orderBy,
				orderDirection: query.orderDirection,
				...(query.inflationPartAccepted !== null && { inflationPartAccepted: query.inflationPartAccepted }),
				...(query.withoutFirmHeader && { withoutFirmHeader: true }),
				...(!query.withoutFirmHeader && query.priceOfferFirmHeaderIDs.length > 0 && { priceOfferFirmHeaderIDs: query.priceOfferFirmHeaderIDs })
			}
		}))
	}, [dispatch,
		query.search, query.limit, query.page, query.orderBy, query.orderDirection, query.inflationPartAccepted, visibleGenerateItemListModal, query.users, query.priceOfferFirmHeaderIDs, query.withoutFirmHeader])

	useEffect(() => {
		if (!filterInitialValues || isEmpty(filterInitialValues)) {
			const initData = {
				search: query.search || null
			}
			dispatch(initialize(FORM.JOBS_FILTER, initData))
		}
		setSelectedRowKeys([])
	}, [
		dispatch, filterInitialValues,
		query.search
	])

	const columnsToPrint: Columns = [
		{
			title: i18next.t('pages:Adresa'),
			dataIndex: 'address',
			key: 'address',
			ellipsis: true,
			width: 110,
			render: (value) => value || '-'
		},
		{
			title: i18next.t('pages:Zodpovedné osoby'),
			dataIndex: 'trustees',
			key: 'trustees',
			ellipsis: true,
			width: 250,
			render: renderTrustees
		}]

	const columns: Columns = [
		{
			title: i18next.t('pages:Adresa'),
			dataIndex: 'address',
			key: 'address',
			ellipsis: true,
			width: 110,
			render: (value) => value || '-'
		},
		{
			title: i18next.t('pages:Zodpovedné osoby'),
			dataIndex: 'trustees',
			key: 'trustees',
			ellipsis: true,
			width: 250,
			render: renderTrustees
		},
		{
			title: i18next.t('pages:s.r.o.'),
			dataIndex: 'firmHeader',
			key: 'firmHeader',
			ellipsis: true,
			width: 170,
			render(value: IJobFirmHeader | null) {
				return value?.firmName || '-'
			}
		},
		{
			title: i18next.t('pages:Trvanie'),
			dataIndex: 'qrScan',
			key: 'qrScan',
			ellipsis: true,
			align: 'center',
			width: 30,
			render: renderQrScanDuration
		},
		{
			title: i18next.t('pages:Posledná služba'),
			dataIndex: 'qrScan',
			key: 'qrScan',
			ellipsis: true,
			align: 'right',
			width: 30,
			render: renderQrScanLastService
		}
	]

	const handleTableChange = (pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<JobTableItem> | SorterResult<JobTableItem>[]) => {
		let order = {}
		if ((sorter as SorterResult<any>)?.order) {
			const sorterLocal = (sorter as SorterResult<any>)
			order = {
				orderBy: sorterLocal.field,
				orderDirection: sorterLocal.order === 'ascend' ? 'asc' : 'desc'
			}
		}

		// @ts-ignore
		setQuery({
			...query,
			limit: PAGINATION.defaultPageSize,
			page: pagination.current,
			...order
		})
	}

	const debounced = useCallback(debounce((searchTerm: string) => setQuery({ ...query, search: searchTerm }), 300), [query])

	const handleOnChange = ((e: React.ChangeEvent<HTMLInputElement>) => {
		debounced(e.target.value)
	})

	// suhrn mesacneho obratu - zobrazi sa len ked je vybrana konkretna s.r.o.
	const selectedHeaderIds = (query.priceOfferFirmHeaderIDs || []) as number[]
	const showTurnoverSummary = !query.withoutFirmHeader && selectedHeaderIds.length > 0
	const selectedHeaderNames = headerOptions.filter((header) => selectedHeaderIds.includes(header.id)).map((header) => header.firmName)
	const turnoverLabel = selectedHeaderNames.length === 1 ? selectedHeaderNames[0] : `${selectedHeaderIds.length} s.r.o.`
	const monthlyTurnoverTotal = (jobs.pagination as any)?.monthlyTurnoverTotal || 0
	const turnoverJobsCount = jobs.pagination?.totalCount || 0
	const avgTurnoverPerJob = turnoverJobsCount ? monthlyTurnoverTotal / turnoverJobsCount : 0
	const formatEur = (value: number) => `${(Math.round(value * 100) / 100).toLocaleString('sk-SK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`

	const componentRef = useRef(null)
	const handlePrint = useReactToPrint({
		content: () => componentRef.current
	})

	// @ts-ignore
	return (
		<>
			<div className={'page-wrapper'}>
				<div>
					{selectedRowKeys?.length > 0 ? <div className={'flex'} style={{ flexWrap: 'wrap' }}>
						<Card
							hoverable
							className={'w-full max-w-full flex-center'}
							size={'small'}
							style={{
								width: '100%', minHeight: '80px', display: 'flex', justifyContent: 'space-between'
							}}
							extra={ <div className={'flex-center'} style={{ minHeight: '60px', display: 'flex', justifyContent: 'space-between' }}>
								<CloseOutlined
									style={{ fontSize: 30, marginRight: '20px' }}
									onClick={ () => setSelectedRowKeys([]) }
								/>
								<span className={'font-bold'}>{t('pages:Vybrané ')} {selectedRowKeys.length}  </span>
							</div>
							}
						>
							<Button
								icon={ <FilePdfOutlined/> }
								onClick={ () => setIsVisibleHandoverMergeRecordModal(true) }
								type={ 'primary' }
								style={ { marginTop: '20px', marginLeft: '20px', marginRight: '20px' } }
							>
								{ t('components:Zlúčiť preberacie protokoly') }
							</Button>
							<Button
								icon={ <FilePdfOutlined/> }
								onClick={ () => setIsVisibleHandoverRecordModal(true) }
								type={ 'primary' }
								style={ { marginTop: '20px' } }
							>
								{ t('components:Preberací protokol') }
							</Button>
						</Card>
					</div> : <div className={'flex'} style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-start', gap: 16, minHeight: '80px' }}>
						<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, flex: '1 1 auto', minWidth: 0 }}>
							<Input.Search onChange={handleOnChange} style={{ width: 260 }} allowClear />
							<SelectUsersField
								ref={userSelectRef}
								onValueChange={(data) => {
									setQuery({
										...query,
										users: data.value.map((item) => (item.value))
									})
								}}
								userValues={query.users}
								multiple={false}
								placeholder={t('pages:Vyber zamestnanca')}
								queryParams={{ role: [USER_ROLE.USER, USER_ROLE.ADMIN, USER_ROLE.SUPERADMIN] }}
							/>
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								<span className={'font-semibold'}>{'Len s aktívnou inflačnou doložkou'}</span>
								<Switch
									onChange={changeInflationFilter}
									checked={query?.inflationPartAccepted || false}
								/>
							</div>
							<Select
								mode={'multiple'}
								allowClear
								showSearch
								style={{ minWidth: 240 }}
								placeholder={t('pages:Filter podľa s.r.o.')}
								value={query.withoutFirmHeader ? [WITHOUT_FIRM_HEADER] : ((query.priceOfferFirmHeaderIDs || []) as number[])}
								optionFilterProp={'children'}
								filterOption={(input, option) => String(option?.children ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
								onChange={(values: number[]) => {
									const hasWithout = values.includes(WITHOUT_FIRM_HEADER)
									const specifics = values.filter((value) => value !== WITHOUT_FIRM_HEADER)
									if (hasWithout && specifics.length > 0) {
										// "Bez s.r.o." a konkretna s.r.o. sa vylucuju - podla toho co uz bolo zvolene
										if (query.withoutFirmHeader) {
											setQuery({ ...query, withoutFirmHeader: null, priceOfferFirmHeaderIDs: specifics, page: 1 })
										} else {
											setQuery({ ...query, withoutFirmHeader: true, priceOfferFirmHeaderIDs: [], page: 1 })
										}
									} else if (hasWithout) {
										setQuery({ ...query, withoutFirmHeader: true, priceOfferFirmHeaderIDs: [], page: 1 })
									} else {
										setQuery({ ...query, withoutFirmHeader: null, priceOfferFirmHeaderIDs: specifics, page: 1 })
									}
								}}
							>
								<Select.Option key={'without'} value={WITHOUT_FIRM_HEADER}>
									{t('pages:Bez s.r.o.')}
								</Select.Option>
								{headerOptions.map((header) => (
									<Select.Option key={header.id} value={header.id}>
										{header.firmName}
									</Select.Option>
								))}
							</Select>
						</div>
						<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'flex-end', gap: 8, flex: '0 1 auto' }}>
							<Popover
								content={
									query.users && query.users.length > 0
										? 'Pre vytvorenie preberacieho protokolu označte aspoň jednu zákazku'
										: 'Pre použitie preberacieho protokolu vyberte zamestnanca'
								}
								trigger="hover"
							>
								<div style={{ display: 'inline-block' }}>
									<Button
										icon={<FilePdfOutlined />}
										disabled={false}
										type="primary"
										onClick={() => {
											if (!query.users || query.users.length === 0) {
												userSelectRef.current?.focus?.()
												userSelectRef.current?.open?.() // ak SelectUsersField podporuje otvorenie
											}
										}}
									>
										{t('pages:Preberací protokol')}
									</Button>
								</div>
							</Popover>
							{query.inflationPartAccepted ? (
								<Button
									icon={<PlusCircleOutlined />}
									type={'primary'}
									onClick={() => setVisibleGenerateItemListModal(true)}
								>
									{t('pages:Vytlač zoznam zákaziek')}
								</Button>
							) : <Button
								icon={<QrcodeOutlined />}
								type={'primary'}
								onClick={() => setVisibleGenerateQrCodeModal(true)}
							>
								{t('pages:Generuj Qr Kody')}
							</Button>}
							<Button
								icon={<BarChartOutlined />}
								type={'primary'}
								onClick={() => history.push('/prace/obraty-sro')}
							>
								{t('pages:Obraty po s.r.o.')}
							</Button>
							<Button
								icon={<PlusCircleOutlined />}
								href={t('paths:jobCreate|path')}
								type={'primary'}
							>
								{t('pages:Pridať Zákazku')}
							</Button>
						</div>
					</div>}
				</div>
				{showTurnoverSummary && (
					<div className={'flex'} style={{ gap: 12, marginTop: 8, marginBottom: 16 }}>
						<Card size={'small'} style={{ flex: 1 }}>
							<div style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.45)' }}>{t('pages:Mesačný obrat')} · {turnoverLabel}</div>
							<div style={{ fontSize: 24, fontWeight: 500, marginTop: 4 }}>{formatEur(monthlyTurnoverTotal)}</div>
						</Card>
						<Card size={'small'} style={{ flex: 1 }}>
							<div style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.45)' }}>{t('pages:Počet zákaziek')}</div>
							<div style={{ fontSize: 24, fontWeight: 500, marginTop: 4 }}>{turnoverJobsCount}</div>
						</Card>
						<Card size={'small'} style={{ flex: 1 }}>
							<div style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.45)' }}>{t('pages:Priemer / zákazka')}</div>
							<div style={{ fontSize: 24, fontWeight: 500, marginTop: 4 }}>{formatEur(avgTurnoverPerJob)}</div>
						</Card>
					</div>
				)}
				<Table
					rowSelection={query.users.length > 0 ? rowSelection : undefined}
					className={'general-table'}
					columns={columns}
					dataSource={jobs.tableData}
					onChange={handleTableChange}
					style={{ marginTop: 0 }}
					showSorterTooltip={false}
					pagination={{
						pageSize: PAGINATION.defaultPageSize,
						total: jobs.pagination?.totalCount,
						current: jobs.pagination?.page,
						showSizeChanger: false
					}}
					loading={isLoadingList}
					onRow={(record) => ({
						onClick: () => history.push(`${t('paths:jobDetail|path')}/${get(record, 'id')}`)
					})}
					locale={{
						emptyText: (
							<Empty description={t('pages:Žiadne dáta')} />
						)
					}}
					size={'small'}
				/>
			</div>
			<Modal
				title={t('pages:Vytlač qr kódy')}
				centered
				visible={visibleGenerateQrCodeModal}
				onOk={() => {
					setVisibleGenerateQrCodeModal(false)
					if (handlePrint) {
						handlePrint()
					}
				}}
				onCancel={() => setVisibleGenerateQrCodeModal(false)}
				width={1000}
			>
				<Button
					type={'primary'}
					onClick={handlePrint}
				>
					{t('pages:Vytlačiť')}
				</Button>
				<ComponentToPrint
					ref={componentRef} list={jobs.tableData.map((data) => ({
						name: data.address,
						data: {
							jobID: data.id,
							jobName: data.address
						}
					}))}
				/>
			</Modal>
			<Modal
				title={t('pages:Vytlač zoznam zákaziek')}
				centered
				visible={visibleGenerateItemListModal}
				onOk={() => {
					setVisibleGenerateItemListModal(false)
					if (handlePrint) {
						handlePrint()
					}
				}}
				onCancel={() => setVisibleGenerateItemListModal(false)}
				width={1000}
			>
				<Button
					type={'primary'}
					onClick={handlePrint}
				>
					{t('pages:Vytlačiť')}
				</Button>
				<TableToPrint ref={componentRef}>
					<Table
						className={'general-table'}
						columns={columnsToPrint}
						dataSource={jobs.tableData}
						onChange={handleTableChange}
						style={{ marginTop: 0 }}
						showSorterTooltip={false}
						pagination={false}
						locale={{
							emptyText: (
								<Empty description={t('pages:Žiadne dáta')} />
							)
						}}
						size={'small'}
					/>
				</TableToPrint>
			</Modal>
			<Modal
				title={ i18next.t('pages:Vytvoriť preberací protokol') }
				visible={isVisibleHandoverRecordModal}
				okText={i18next.t('pages:Vytvoriť')}
				onOk={() => {
					if (!headerValue) {
						notification.error({
							message: i18next.t('pages:Vyberte hlavičku')
						})
						return
					}
					handleCreateHandoverRecord(selectedRowKeys.map((key: React.Key) => key as number), headerValue)
				}}
				onCancel={ () => setIsVisibleHandoverRecordModal(false)} >
				<div className={'mb-4'}>
					<span className={'mr-2'}>{i18next.t('pages:Chýbajúce údaje môžete doplniť v nastaveniach')}</span>
					<Link to={ t('paths:appSettings|path').toString()}>
						{ i18next.t('pages:Nastavenia') }
					</Link>
				</div>
				<SelectPriceOfferHeaderOptionField
					onValueChange={setHeaderValue}
					headerValue={headerValue}
				/>
			</Modal>
			<Modal
				title={ i18next.t('pages:Vytvoriť zlúčený preberací protokol') }
				visible={isVisibleHandoverMergeRecordModal}
				okText={i18next.t('pages:Vytvoriť')}
				onOk={() => {
					if (!headerValue) {
						notification.error({
							message: i18next.t('pages:Vyberte hlavičku')
						})
						return
					}
					handleCreateMergeHandoverRecord(selectedRowKeys.map((key: React.Key) => key as number), headerValue)
				}}
				onCancel={ () => setIsVisibleHandoverMergeRecordModal(false)} >
				<div className={'mb-4'}>
					<span className={'mr-2'}>{i18next.t('pages:Chýbajúce údaje môžete doplniť v nastaveniach')}</span>
					<Link to={ t('paths:appSettings|path').toString()}>
						{ i18next.t('pages:Nastavenia') }
					</Link>
				</div>
				<SelectPriceOfferHeaderOptionField
					onValueChange={setHeaderValue}
					headerValue={headerValue}
				/>
			</Modal>
		</>
	)
}

JobsStandard.propTypes = {}

/**
 * Rovnaka URL /prace, dva obsahy. Tag v hlavicke neprepina stranku, prepina zdroj dat.
 * Wrapper (a nie early return v JobsStandard) preto, aby sa neporusili pravidla hookov.
 */
const Jobs = () => {
	const { isActive } = useSuperOrganizationMode()

	return isActive ? <SuperOrganizationJobs /> : <JobsStandard />
}

export default Jobs
